import type React from "react"
import { useRBAC } from "@/lib/rbac/context"
import { Unauthorized } from "@/components/Unauthorized"
import { StandardSkeleton } from "@/components/StandardSkeleton"
import { FunErrorBoundary } from "@/components/error/FunErrorBoundary"

// HOC to protect pages
export function withPageProtection<P extends object>(Component: React.ComponentType<P>, pageName: string) {
  return function ProtectedPage(props: P) {
    const { hasPermission, isLoading } = useRBAC()

    if (isLoading) {
      return <StandardSkeleton />
    }
    if (!hasPermission("page", pageName, "view")) {
      return <Unauthorized />
    }
    return <FunErrorBoundary><Component {...props} /></FunErrorBoundary>
  }
}

// HOC to protect components
export function withComponentProtection<P extends object>(
  Component: React.ComponentType<P>,
  componentName: string,
  action?: string,
  fallback?: React.ReactNode,
) {
  return function ProtectedComponent(props: P) {
    const { hasPermission, isLoading } = useRBAC()

    if (isLoading) {
      return null
    }

    if (!hasPermission("component", componentName, action)) {
      return fallback ? <>{fallback}</> : null
    }

    return <FunErrorBoundary><Component {...props} /></FunErrorBoundary>
  }
}

// HOC to protect attributes
export function withAttributeProtection<P extends object>(
  Component: React.ComponentType<P>,
  attributeName: string,
  action?: string,
  fallback?: React.ReactNode,
) {
  return function ProtectedAttribute(props: P) {
    const { hasPermission, isLoading } = useRBAC()

    if (isLoading) {
      return null
    }

    if (!hasPermission("attribute", attributeName, action)) {
      return fallback ? <>{fallback}</> : null
    }

    return <FunErrorBoundary><Component {...props} /></FunErrorBoundary>
  }
}
